import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown, FilePreview } from "components";
import { Attachments } from "components/FileDropdown/components";
import "components/FileDropdown/FileDropdown.scss";
import { downloadCloudBucketResources } from "utils/common";

import type { Attachment } from "components/FileDropdown/components";

type FileDropdownProps = {
  fromAPI?: boolean;
  iconColor?: string;
  files?: Attachment[];
};

export const FileDropdown = ({ files, iconColor = "#21A1D0", fromAPI = false }: FileDropdownProps) => {
  const { t } = useTranslation();

  const filePreviewContentRef = useRef<HTMLDivElement>(null);
  const [shownPreviewIndex, setShownPreviewIndex] = useState<number | null>(null);
  const [selectedFileUrls, setSelectedFileUrls] = useState<string[]>([]);

  useEffect(() => {
    filePreviewContentRef.current?.focus();
  }, [filePreviewContentRef.current]);

  const handlePreviewAttachment = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    e.stopPropagation();
    setShownPreviewIndex(index);
  };

  const handleSelectAll = (e: React.FormEvent<HTMLInputElement>) => {
    if (!files) return;

    e.stopPropagation();
    const isAllSelected = selectedFileUrls.length === files.length;
    setSelectedFileUrls(isAllSelected ? [] : files.map(file => file.url));
  };

  const handleDownloadFiles = async () => {
    await downloadCloudBucketResources(selectedFileUrls);
    setSelectedFileUrls([]);
  };

  if (!files?.length) return null;

  return (
    <div className="AppointmentNoteAttachments">
      <Dropdown
        multiple
        className="action-dropdown"
        floating
        icon={null}
        trigger={
          <div className="appointment-note-attachments">
            <Icon className="paperclip" style={{ color: iconColor }} />
            <div>{files.length}</div>
          </div>
        }
      >
        <DropdownMenu>
          <DropdownItem>
            <div className="file-drop-select-all-container">
              <Checkbox checked={selectedFileUrls.length === files.length} onChange={handleSelectAll} className="files-url-checkbox" />
              <span>{t("v8_select_all", "Select all")}</span>
            </div>
          </DropdownItem>

          <Attachments
            fromAPI={fromAPI}
            attachments={files}
            selectedFileUrls={selectedFileUrls}
            setSelectedFileUrls={setSelectedFileUrls}
            handlePreviewAttachment={handlePreviewAttachment}
          />

          {selectedFileUrls.length > 0 && (
            <Button onClick={handleDownloadFiles} color="green" className="files-url-download-btn">
              {t("v8_download", "Download")}
            </Button>
          )}
        </DropdownMenu>
      </Dropdown>

      <FilePreview files={files} previewIndex={shownPreviewIndex} setPreviewIndex={setShownPreviewIndex} />
    </div>
  );
};
